import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Lottie from "lottie-react";

import Select from "./Select";
import * as contactLoading from "../../animations/profile.json";
import contactOptionsets from "../../options/contact.json";
import {
  getFieldFormattedValue,
  getFieldName,
  updateEntity,
} from "./../../scripts/helperScripts";
import { getContact } from "../services/soar";
import Checkbox from "./Checkbox";

function DemographicsModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  clientId,
}) {
  const [demographics, setDemographics] = useState();
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    getContact(clientId).then((results) => {
      if (results && isMountedRef.current) {
        const contact = results[0];
        if (contact) {
          const demo = {
            sor_consideryourselfhispaniclatino:
              getFieldName(contact["sor_consideryourselfhispaniclatino"]) ===
              "Yes",
            sor_americanindianoralaskanative:
              getFieldName(contact["sor_americanindianoralaskanative"]) ===
              "Yes",
            sor_nativehawaiianorotherpacificislander:
              getFieldName(
                contact["sor_nativehawaiianorotherpacificislander"]
              ) === "Yes",
            sor_blackorafricanamerican:
              getFieldName(contact["sor_blackorafricanamerican"]) === "Yes",
            sor_white: getFieldName(contact["sor_white"]) === "Yes",
            sor_asian: getFieldName(contact["sor_asian"]) === "Yes",
            sor_primarylanguage: getFieldFormattedValue(
              contact["sor_primarylanguage"]
            ),
          };
          setDemographics(demo);
        }
      }
    });
  }, [clientId]);

  const handleChecked = ({ currentTarget: input }) => {
    const data = { ...demographics };
    data[input.name] = input.checked;
    setDemographics(data);
  };

  const handleChange = ({ currentTarget: input }) => {
    const data = { ...demographics };
    data[input.name] = input.value;
    setDemographics(data);
  };

  const handleSave = () => {
    const toastId = toast.loading("Saving Demographics...", {
      type: toast.TYPE.INFO,
      closeOnClick: true,
    });

    const obj = {
      sor_consideryourselfhispaniclatino: {
        type: "bool",
        value: demographics.sor_consideryourselfhispaniclatino,
      },
      sor_americanindianoralaskanative: {
        type: "bool",
        value: demographics.sor_americanindianoralaskanative,
      },
      sor_nativehawaiianorotherpacificislander: {
        type: "bool",
        value: demographics.sor_nativehawaiianorotherpacificislander,
      },
      sor_blackorafricanamerican: {
        type: "bool",
        value: demographics.sor_blackorafricanamerican,
      },
      sor_white: { type: "bool", value: demographics.sor_white },
      sor_asian: { type: "bool", value: demographics.sor_asian },
      sor_primarylanguage: {
        type: "optionset",
        value: demographics.sor_primarylanguage,
      },
    };

    updateEntity("contact", clientId, JSON.stringify(obj), safyInfo)
      .then(() => {
        toast.update(toastId, {
          render: "Demographics have been saved.",
          isLoading: false,
          type: toast.TYPE.SUCCESS,
          autoClose: 5000,
          closeOnClick: true,
        });
      })
      .catch(() => {
        toast.update(toastId, {
          render: "An error occurred saving Demographics.",
          isLoading: false,
          type: toast.TYPE.ERROR,
          autoClose: 5000,
          closeOnClick: true,
        });
      });
  };

  const resetForm = () => {
    setDemographics(undefined);
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>Demographics</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {demographics === undefined ? (
              <>
                <div className="d-flex justify-content-center align-items-center">
                  <h1>LOADING CLIENT INFO</h1>
                  <Lottie
                    animationData={contactLoading.default}
                    loop={true}
                    autoplay={true}
                    rendererSettings={{
                      preserveAspectRatio: "xMidYMid slice",
                    }}
                    height={120}
                    width={120}
                  />
                </div>
              </>
            ) : null}
            {demographics !== undefined ? (
              <>
                <div className="ms-4">
                  <Checkbox
                    name="sor_consideryourselfhispaniclatino"
                    label="Considered Hispanic or Latino"
                    checked={
                      demographics.sor_consideryourselfhispaniclatino === true
                    }
                    onChange={handleChecked}
                  />
                  <Checkbox
                    name="sor_americanindianoralaskanative"
                    label="American Indian or Alaskan Native"
                    checked={demographics.sor_americanindianoralaskanative}
                    onChange={handleChecked}
                  />
                  <Checkbox
                    name="sor_nativehawaiianorotherpacificislander"
                    label="Native Hawaiian or Pacific Islander"
                    checked={
                      demographics.sor_nativehawaiianorotherpacificislander
                    }
                    onChange={handleChecked}
                  />
                  <Checkbox
                    name="sor_blackorafricanamerican"
                    label="Black or African American"
                    checked={demographics.sor_blackorafricanamerican}
                    onChange={handleChecked}
                  />
                  <Checkbox
                    name="sor_white"
                    label="White"
                    checked={demographics.sor_white}
                    onChange={handleChecked}
                  />
                  <Checkbox
                    name="sor_asian"
                    label="Asian"
                    checked={demographics.sor_asian}
                    onChange={handleChecked}
                  />
                </div>
                <Select
                  name="sor_primarylanguage"
                  label="Primary Language"
                  options={
                    contactOptionsets["sor_primarylanguage"] &&
                    contactOptionsets["sor_primarylanguage"]
                  }
                  value={demographics.sor_primarylanguage}
                  onChange={handleChange}
                />
              </>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            {nextModal === undefined && (
              <>
                <button className="btn btn-danger" onClick={handleHideModal}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleSave();
                    handleHideModal();
                  }}
                >
                  Submit
                </button>
              </>
            )}
            {nextModal && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetForm();
                    nextModal();
                  }}
                >
                  Skip
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleSave();
                    resetForm();
                    nextModal();
                  }}
                >
                  Submit and {isLastModal ? "End" : "Next"}
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default DemographicsModal;
